import React, { ReactElement, useState } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import FilterItem from '@components/music/filter/FilterItem'
import { getParamCategory } from '@components/music/filter/utils/helpers'
import { Facet } from '@components/music/filter/utils/types'
import useUpdateQueryParams from '@hooks/useUpdateQueryParams'
import { usePageApi } from '@providers/PageApiProvider'
import { SearchPageApiResponse } from '@providers/types/api-responses'

const DEFAULT_NUM_ACCORDION_ITEMS = 5

interface Props {
  facet: Facet
  isFilterOpen: boolean
  onCollapseChange?: () => void
  hideExpander?: boolean
  className?: string
}

const FacetTermList = React.forwardRef<HTMLUListElement, Props>(
  ({ facet, isFilterOpen, onCollapseChange, hideExpander, className }: Props, ref) => {
    const { t } = useTranslation()
    const { params } = useUpdateQueryParams()
    const { pageData } = usePageApi<SearchPageApiResponse>()

    const [expanded, setExpanded] = useState<boolean>(false)

    const { terms } = facet
    const paramCategory = getParamCategory(facet)

    const generateFilterItems = (): ReactElement[] | ReactElement | null => {
      let termsDup = [...terms]
      const allComponent = hideExpander ? null : (
        <li className="text-md pb-1.5 last:pb-0">{t('content.all')}</li>
      )

      // If there are NO filters selected and the accordion is collapsed this component is displayed
      if (
        !(paramCategory in params) &&
        pageData?.slug_filter?.category !== facet.category &&
        !isFilterOpen
      ) {
        return allComponent
      }

      // If the filter isn't open but there are filters currently selected show those!
      if (!isFilterOpen) {
        termsDup = termsDup.filter(
          (f) =>
            params[paramCategory]?.includes(f.key) ||
            (pageData?.slug_filter?.category === facet.category &&
              pageData.slug_filter.value.toString() === f.key)
        )
        if (!termsDup.length) return allComponent
      }

      // Limit the number of list items if expanded otherwise show all of them
      const numFiltersItems =
        expanded || hideExpander ? termsDup.length : DEFAULT_NUM_ACCORDION_ITEMS
      termsDup = termsDup.slice(0, numFiltersItems)

      // Loop through the filters and make UI components!
      return termsDup.map((filter) => (
        <FilterItem key={filter.key} facet={facet} term={filter} isFilterOpen={isFilterOpen} />
      ))
    }

    return (
      <ul className={classNames('last:pb-0', className)} ref={ref}>
        {generateFilterItems()}
        {/* Don't show the expansion button if the number of children
              are less or equal to the number of items allowed */}
        {isFilterOpen && !hideExpander && terms.length > DEFAULT_NUM_ACCORDION_ITEMS && (
          <button
            className="mt-1 flex cursor-pointer items-center text-orange hover:text-orange-dark"
            onClick={() => {
              setExpanded(!expanded)
              if (typeof onCollapseChange === 'function') onCollapseChange()
            }}>
            {expanded ? t('action.less') : t('action.more')}
            <i
              className={classNames(
                'transition ease-in duration-300 hover:opacity-50 nf-icon nf-chevron-down text-xl leading-[0.5rem]',
                { 'rotate-180': expanded }
              )}
            />
          </button>
        )}
      </ul>
    )
  }
)

export default FacetTermList
